import { RESOURCES, type PermissionKey, type Resource, type ScopeKind } from './permissions.js';

export type RoleSlug =
  | 'owner'
  | 'admin'
  | 'pastor'
  | 'secretary'
  | 'congregation_coordinator'
  | 'ministry_leader'
  | 'scheduling_assistant'
  | 'volunteer'
  | 'kids_leader'
  | 'kids_receptionist'
  | 'kids_teacher'
  | 'guardian'
  | 'viewer'
  | 'platform_support';

export interface RolePreset {
  slug: RoleSlug;
  name: string;
  description: string;
  /** Escopo em que o perfil normalmente e atribuido. */
  defaultScope: ScopeKind;
  permissions: readonly PermissionKey[];
}

const READ_ALL: readonly PermissionKey[] = [
  'people:read',
  'ministries:read',
  'functions:read',
  'events:read',
  'positions:read',
  'assignments:read',
  'service_plans:read',
  'songs:read',
  'files:read',
  'reports:read',
];

/**
 * Perfis pre-definidos — secao 8.2. Sao o ponto de partida do tenant; ele pode
 * editá-los e criar os seus. A permissão child_health:read só aparece onde o
 * cuidado da criança exige, nunca em perfil administrativo genérico.
 */
export const ROLE_PRESETS: readonly RolePreset[] = [
  {
    slug: 'owner',
    name: 'Proprietário',
    description: 'Controle total da conta, inclusive faturamento e exclusão do tenant.',
    defaultScope: 'tenant',
    permissions: [
      'people:manage',
      'ministries:manage',
      'functions:manage',
      'events:manage',
      'positions:manage',
      'assignments:manage',
      'swaps:manage',
      'approvals:manage',
      'checkins:manage',
      'service_plans:manage',
      'songs:manage',
      'files:manage',
      'notifications:manage',
      'whatsapp:manage',
      'reports:manage',
      'settings:manage',
      'billing:manage',
      'roles:manage',
      'audit:read',
      'tenants:delete',
      /*
       * O infantil também é administrado pela igreja: salas, cadastro de
       * criança, responsáveis e habilitação de quem serve. `child_health:*`
       * fica de fora de propósito — nem o dono da conta vê ficha de saúde sem
       * receber a permissão explicitamente (seção 8.1).
       */
      'children:manage',
      'guardians:manage',
      'child_checkins:manage',
      'child_incidents:manage',
      'kids_clearances:manage',
    ],
  },
  {
    slug: 'admin',
    name: 'Administrador',
    description: 'Tudo, menos contratar plano e excluir a igreja.',
    defaultScope: 'tenant',
    permissions: [
      'people:manage',
      'ministries:manage',
      'functions:manage',
      'events:manage',
      'positions:manage',
      'assignments:manage',
      'swaps:manage',
      'approvals:manage',
      'checkins:manage',
      'service_plans:manage',
      'songs:manage',
      'files:manage',
      'notifications:manage',
      'whatsapp:manage',
      'reports:manage',
      'settings:manage',
      // O administrador vê a assinatura e as faturas; contratar, trocar de
      // plano e cancelar continua sendo do dono da conta.
      'billing:read',
      'roles:manage',
      'audit:read',
      // Mesmo aqui: administra o infantil, não vê a ficha de saúde.
      'children:manage',
      'guardians:manage',
      'child_checkins:manage',
      'child_incidents:manage',
      'kids_clearances:manage',
    ],
  },
  {
    slug: 'pastor',
    name: 'Pastor',
    description: 'Leitura ampla, aprovação de escalas, comunicação e relatórios.',
    defaultScope: 'tenant',
    permissions: [
      ...READ_ALL,
      'approvals:approve',
      'assignments:approve',
      'notifications:create',
      'reports:export',
    ],
  },
  {
    slug: 'secretary',
    name: 'Secretaria',
    description: 'Pessoas, eventos, escalas, comunicação e relatórios.',
    defaultScope: 'tenant',
    permissions: [
      'people:manage',
      'events:manage',
      'positions:manage',
      'assignments:create',
      'assignments:read',
      'assignments:update',
      'assignments:publish',
      'notifications:create',
      'reports:read',
      'reports:export',
      'ministries:read',
      'functions:read',
      'files:read',
      'files:create',
    ],
  },
  {
    slug: 'congregation_coordinator',
    name: 'Coordenador de congregação',
    description: 'Tudo dentro da sua congregação.',
    defaultScope: 'congregation',
    permissions: [
      'people:manage',
      'events:manage',
      'positions:manage',
      'assignments:manage',
      'swaps:approve',
      'approvals:approve',
      'checkins:manage',
      'service_plans:manage',
      'notifications:create',
      'reports:read',
      'ministries:read',
      'functions:read',
    ],
  },
  {
    slug: 'ministry_leader',
    name: 'Líder de departamento',
    description: 'Escalar, publicar e comunicar dentro do seu departamento.',
    defaultScope: 'ministry',
    permissions: [
      'assignments:manage',
      'assignments:publish',
      'positions:manage',
      'swaps:approve',
      'checkins:create',
      'checkins:update',
      'service_plans:manage',
      'songs:manage',
      'files:create',
      'files:read',
      'notifications:create',
      'people:read',
      'functions:read',
      'ministries:read',
      'events:read',
      'reports:read',
    ],
  },
  {
    slug: 'scheduling_assistant',
    name: 'Auxiliar de escala',
    description: 'Monta rascunho de escala; não publica.',
    defaultScope: 'ministry',
    permissions: [
      'assignments:create',
      'assignments:update',
      'assignments:read',
      'people:read',
      'events:read',
      'positions:read',
      'functions:read',
    ],
  },
  {
    slug: 'volunteer',
    name: 'Voluntário',
    description: 'Vê suas escalas, confirma, recusa, pede troca e bloqueia datas.',
    defaultScope: 'self',
    permissions: [
      'assignments:read',
      'assignments:update',
      'swaps:create',
      'events:read',
      'service_plans:read',
      'files:read',
      'people:read',
    ],
  },
  {
    slug: 'kids_leader',
    name: 'Líder do Ministério Infantil',
    description:
      'Todo o kids: crianças, fichas de saúde, salas, retirada excepcional e relatórios.',
    defaultScope: 'tenant',
    permissions: [
      'children:manage',
      'child_health:manage',
      'child_checkins:manage',
      'child_checkins:override',
      'child_incidents:manage',
      'guardians:manage',
      'kids_clearances:manage',
      'children:export',
      'reports:read',
      'people:read',
      'events:read',
      'assignments:read',
    ],
  },
  {
    slug: 'kids_receptionist',
    name: 'Recepcionista do kids',
    description:
      'Check-in, check-out e etiquetas. Não edita ficha de saúde nem libera retirada excepcional.',
    defaultScope: 'tenant',
    permissions: [
      'children:read',
      'child_checkins:create',
      'child_checkins:read',
      'child_checkins:update',
      'guardians:read',
      'events:read',
    ],
  },
  {
    slug: 'kids_teacher',
    name: 'Professor do kids',
    description: 'Vê a sala do evento em que está escalado; o acesso expira com o evento.',
    defaultScope: 'classroom',
    permissions: [
      'children:read',
      'child_health:read',
      'child_incidents:create',
      'child_incidents:read',
      'child_checkins:read',
    ],
  },
  {
    slug: 'guardian',
    name: 'Responsável',
    description: 'Só as próprias crianças: ficha, status ao vivo, código de retirada e histórico.',
    defaultScope: 'guardian',
    permissions: [
      'children:read',
      'children:update',
      'child_health:read',
      'child_health:update',
      'child_checkins:read',
      'child_incidents:read',
      'children:export',
    ],
  },
  {
    slug: 'viewer',
    name: 'Visualizador',
    description: 'Somente leitura das escalas publicadas.',
    defaultScope: 'tenant',
    permissions: ['events:read', 'assignments:read', 'people:read'],
  },
  {
    slug: 'platform_support',
    name: 'Suporte El Shaday',
    description: 'Escopo de plataforma; acesso a tenant apenas por impersonação auditada.',
    defaultScope: 'platform',
    permissions: [
      'tenants:read',
      'tenants:update',
      'billing:manage',
      'audit:read',
      'tenants:impersonate',
    ],
  },
];

/**
 * O que a equipe da El Shaday pode fazer **dentro** de uma igreja, durante uma
 * impersonação auditada (M17).
 *
 * Só leitura. Impersonação é para **ver o que a igreja vê** — diagnosticar por
 * que a escala não publicou, por que a mensagem não saiu. Nunca para agir em
 * nome dela: o que precisa mudar, a igreja muda; o que é da conta, o backoffice
 * muda pelas rotas dele, que já são auditadas.
 *
 * **A ficha de saúde da criança fica de fora**, e ficará sempre. Nenhum suporte
 * precisa dela para resolver um chamado, e a ADR 0009 é clara: quem vê saúde é
 * quem cuida daquela criança naquele culto.
 */
export const PERMISSOES_DO_SUPORTE: readonly PermissionKey[] = (RESOURCES as readonly Resource[])
  .filter((recurso) => recurso !== 'child_health')
  .map((recurso) => `${recurso}:read` as PermissionKey);

export const ROLE_PRESETS_BY_SLUG: Record<RoleSlug, RolePreset> = Object.fromEntries(
  ROLE_PRESETS.map((role) => [role.slug, role]),
) as Record<RoleSlug, RolePreset>;
